import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import DialogContent from "@mui/material/DialogContent";
import CoachProfileViewContainer from "../coachProfileView/CoachProfileViewContainer";
import CoachingTreeContainer from "../CoachingTree/CoachingTreeContainer";
import ProfileEditMainContainer from "../admin/CoachProfileEdit/ProfileEditMainContainer";
import { CoachProfileDialogContext } from "./CoachProfileDialogContext";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  content: {
    background: theme.palette.background.default,
    padding: 0,
    "&:first-child": {
      paddingTop: 0,
    },
  },
}));

// Extract the username segment (after the last `/`, ignoring query string) so
// that in-dialog navigation can switch which coach the inner view loads.
const extractUserNameFromPath = (path, fallback) => {
  if (!path) return fallback;
  const noQuery = path.split("?")[0];
  const parts = noQuery.split("/").filter(Boolean);
  return parts[parts.length - 1] || fallback;
};

// Pick the right container based on the requested in-dialog path.
// We intentionally avoid mounting a nested <Router>/<RouterProvider> here
// because react-router v6/v7 forbids nesting routers. The child containers
// accept a `dialogUsername` prop and read `useCoachProfileDialog()` to drive
// further navigation inside the dialog.
const resolveDialogView = (path, fallbackUsername) => {
  if (!path) return null;
  const lower = path.toLowerCase();
  const userName = extractUserNameFromPath(path, fallbackUsername);
  if (lower.startsWith("/coaches/view/")) {
    return <CoachProfileViewContainer dialogUsername={userName} />;
  }
  if (lower.startsWith("/coaches/tree/")) {
    return <CoachingTreeContainer dialogUsername={userName} />;
  }
  if (lower.startsWith("/admin/coach/edit/")) {
    return <ProfileEditMainContainer dialogUsername={userName} />;
  }
  return null;
};

const CoachProfileDialog = ({ open, onClose, username, initialPath }) => {
  const classes = useStyles();
  const defaultPath = initialPath || ("/Coaches/View/" + username);
  const dialogContentRef = useRef(null);

  const [pathStack, setPathStack] = useState([defaultPath]);
  const currentPath = pathStack[pathStack.length - 1];
  const canGoBack = pathStack.length > 1;

  // Reset to the requested entry path whenever the dialog (re)opens or the
  // caller asks for a different coach / view.
  useEffect(() => {
    if (open) {
      setPathStack([defaultPath]);
    }
  }, [open, defaultPath]);

  // Scroll to top whenever the path changes or the dialog opens
  useEffect(() => {
    if (dialogContentRef.current && open) {
      dialogContentRef.current.scrollTop = 0;
    }
  }, [currentPath, open]);

  const dialogNavigate = useCallback((to) => {
    if (!to) return;
    // Accept absolute string paths only; ignore anything else so we don't
    // accidentally swallow main-app navigations we don't recognise.
    if (typeof to !== "string") return;
    setPathStack((prev) => {
      if (prev[prev.length - 1] === to) return prev;
      return [...prev, to];
    });
  }, []);

  const dialogBack = useCallback(() => {
    setPathStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const contextValue = useMemo(
    () => ({ dialogNavigate, dialogBack, canGoBack, close: onClose }),
    [dialogNavigate, dialogBack, canGoBack, onClose]
  );

  const view = useMemo(
    () => resolveDialogView(currentPath, username),
    [currentPath, username]
  );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Coach Profile
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent className={classes.content} ref={dialogContentRef}>
        {username && (
          <CoachProfileDialogContext.Provider value={contextValue}>
            {view}
          </CoachProfileDialogContext.Provider>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CoachProfileDialog;
